/**
 * React Query hooks for scoring defaults and onboarding.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { mlbApi } from '../services/api';
import { STALE_TIMES, QUERY_RETRY_CONFIG } from '../types/statcast';
import type {
  ScoringDefaultsResponse,
  ScoringDefaultsData,
  OnboardingCompleteResponse,
  OnboardingStatusResponse,
} from '../services/api';

// =============================================================================
// Query Keys
// =============================================================================

export const scoringDefaultsKeys = {
  all: ['scoring-defaults'] as const,
  defaults: () => [...scoringDefaultsKeys.all, 'defaults'] as const,
  onboardingStatus: () => [...scoringDefaultsKeys.all, 'onboarding-status'] as const,
};

// =============================================================================
// Scoring Defaults Query Hooks
// =============================================================================

/**
 * Fetch current user's scoring defaults.
 */
export function useScoringDefaults(enabled = true) {
  const { getToken, isSignedIn } = useAuth();

  return useQuery<ScoringDefaultsResponse>({
    queryKey: scoringDefaultsKeys.defaults(),
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.getScoringDefaults(token);
    },
    staleTime: STALE_TIMES.SCHEDULE,
    enabled: isSignedIn && enabled,
    retry: false, // Don't retry 404s (user hasn't created profile yet during onboarding)
  });
}

/**
 * Fetch onboarding status.
 */
export function useOnboardingStatus() {
  const { getToken, isSignedIn } = useAuth();

  return useQuery<OnboardingStatusResponse>({
    queryKey: scoringDefaultsKeys.onboardingStatus(),
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.getOnboardingStatus(token);
    },
    staleTime: STALE_TIMES.CAREER_STATS,
    enabled: isSignedIn,
    ...QUERY_RETRY_CONFIG,
  });
}

// =============================================================================
// Scoring Defaults Mutation Hooks
// =============================================================================

/**
 * Update scoring defaults.
 */
export function useUpdateScoringDefaults() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<ScoringDefaultsResponse, Error, Partial<ScoringDefaultsData>>({
    mutationFn: async (data) => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.updateScoringDefaults(data, token);
    },
    onSuccess: (data) => {
      queryClient.setQueryData<ScoringDefaultsResponse>(scoringDefaultsKeys.defaults(), data);
    },
  });
}

/**
 * Complete onboarding with scoring defaults.
 */
export function useCompleteOnboarding() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<OnboardingCompleteResponse, Error, Partial<ScoringDefaultsData>>({
    mutationFn: async (data) => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.completeOnboarding(data, token);
    },
    onSuccess: () => {
      // Invalidate onboarding status and defaults
      queryClient.invalidateQueries({ queryKey: scoringDefaultsKeys.onboardingStatus() });
      queryClient.invalidateQueries({ queryKey: scoringDefaultsKeys.defaults() });
      // Also invalidate user profile to update has_completed_onboarding
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
    },
  });
}

/**
 * Skip onboarding.
 */
export function useSkipOnboarding() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<OnboardingCompleteResponse, Error, void>({
    mutationFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.skipOnboarding(token);
    },
    onSuccess: () => {
      // Invalidate onboarding status
      queryClient.invalidateQueries({ queryKey: scoringDefaultsKeys.onboardingStatus() });
      // Also invalidate user profile to update has_completed_onboarding
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
    },
  });
}

// =============================================================================
// Scoring Defaults Option Constants
// =============================================================================

/**
 * Viewing method options for the scoring defaults form.
 */
export const VIEWING_METHOD_OPTIONS = [
  { value: 'in_person', label: 'In Person at Ballpark' },
  { value: 'tv', label: 'TV Broadcast' },
  { value: 'streaming', label: 'Streaming (MLB.tv, etc.)' },
  { value: 'radio', label: 'Radio' },
  { value: 'gameday', label: 'MLB Gameday' },
  { value: 'other', label: 'Other' },
] as const;

/**
 * Scoring tool options.
 */
export const SCORING_TOOL_OPTIONS = [
  { value: 'pencil', label: 'Pencil' },
  { value: 'pen', label: 'Pen' },
  { value: 'digital', label: 'Digital / App' },
] as const;

/**
 * Game prep level options.
 */
export const GAME_PREP_OPTIONS = [
  { value: 'none', label: 'No Prep', description: 'Start with a blank card' },
  { value: 'some', label: 'Some Prep', description: 'Fill in lineups beforehand' },
  { value: 'full', label: 'Full Prep', description: 'Lineups + research / notes' },
] as const;

/**
 * Scoring style level options.
 */
export const SCORING_STYLE_OPTIONS = [
  { value: 'basic', label: 'Basic', description: 'Just tracking plays' },
  { value: 'moderate', label: 'Moderate', description: 'Some extra notation' },
  { value: 'detailed', label: 'Detailed', description: 'Pitch counts, locations, etc.' },
] as const;

/**
 * Scorebook type options.
 */
export const SCOREBOOK_TYPE_OPTIONS = [
  { value: 'purchased', label: 'Purchased Scorebook' },
  { value: 'printed', label: 'Printed Sheets' },
  { value: 'homemade', label: 'Homemade / Custom' },
  { value: 'digital', label: 'Digital / App' },
  { value: 'other', label: 'Other' },
] as const;

/**
 * Purchased scorebook subtype options (shown when scorebook_type is 'purchased').
 */
export const PURCHASED_SCOREBOOK_OPTIONS = [
  { value: 'bob_carpenter', label: "Bob Carpenter's Scorebook" },
  { value: 'eephus_league', label: 'Eephus League Halfliner' },
  { value: 'numbers_22', label: 'Numbers - #22' },
  { value: 'numbers_pocket', label: 'Numbers - Pocket-Sized' },
  { value: 'seven_two_square', label: '7-2 Double Play - The Square' },
  { value: 'seven_two_small_ball', label: '7-2 Double Play - Small Ball' },
  { value: 'other', label: 'Other' },
] as const;
